import { ReactNode, useCallback, useLayoutEffect, useRef } from 'react';
import {
  TextField,
  Label,
  TextArea as AriaTextArea,
  Text as AriaText,
  FieldError,
  type TextFieldProps,
} from 'react-aria-components';
import { cx } from './cx';
import { typography } from './Text';

const boxBase =
  'flex rounded-lg bg-surface border border-strong shadow-xs px-3 py-2.5 ' +
  'text-fg transition ' +
  'focus-within:border-brand-300 focus-within:ring-4 focus-within:ring-[rgb(var(--c-ring)/0.24)] ' +
  'group-data-[invalid]:border-error-500 group-data-[invalid]:focus-within:ring-error-500/24';

export interface TextAreaProps extends Omit<
  TextFieldProps,
  'children' | 'className'
> {
  label?: string;
  placeholder?: string;
  hint?: ReactNode;
  /** Error message; presence also drives the invalid styling. */
  errorMessage?: string;
  /** Visible rows — the minimum height; also the fixed height when not autoResize. */
  rows?: number;
  /**
   * Grow the field to fit its content instead of scrolling internally (the
   * surrounding container should handle overflow). Disables manual resize.
   */
  autoResize?: boolean;
  fullWidth?: boolean;
  className?: string;
}

/**
 * The Regask multiline text input — react-aria `TextField` + `TextArea`,
 * sharing TextInput's anatomy (top label, bordered surface box that lifts to a
 * brand focus ring, hint / error text) for the longer free-text fields a single
 * line can't hold (e.g. a feedback description).
 */
export const TextArea = ({
  label,
  placeholder,
  hint,
  errorMessage,
  rows = 4,
  autoResize = false,
  fullWidth = false,
  className,
  ...props
}: TextAreaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el || !autoResize) return;
    // Collapse first so shrinking (deleting lines) is measured correctly.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [autoResize]);
  // Re-fit whenever the controlled value changes (typing, paste, reset).
  useLayoutEffect(resize, [resize, props.value]);

  return (
    <TextField
      {...props}
      isInvalid={props.isInvalid ?? Boolean(errorMessage)}
      className={cx(
        'group flex flex-col gap-1.5',
        fullWidth && 'w-full',
        className,
      )}
    >
      {label && (
        <Label className={cx(typography.label, 'text-fg-secondary')}>
          {label}
          {props.isRequired && (
            <span className="ml-0.5 text-brand-text">*</span>
          )}
        </Label>
      )}
      <div className={boxBase}>
        <AriaTextArea
          ref={textareaRef}
          rows={rows}
          placeholder={placeholder}
          onInput={resize}
          className={cx(
            typography.body,
            'min-w-0 flex-1 appearance-none border-0 bg-transparent text-fg outline-none break-words placeholder:text-fg-muted',
            autoResize ? 'resize-none overflow-hidden' : 'resize-y',
          )}
        />
      </div>
      {hint && !errorMessage && (
        <AriaText
          slot="description"
          className={cx(typography.body, 'text-fg-tertiary')}
        >
          {hint}
        </AriaText>
      )}
      <FieldError className={cx(typography.body, 'text-error')}>
        {errorMessage}
      </FieldError>
    </TextField>
  );
};
